import React from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, useMediaQuery, Checkbox } from "@mui/material";
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import { Users } from "lucide-react";
import { useSettings } from "../../providers/settings-provider";
import { DropdownOption } from "../ui/dropdown";
import theme from "../../util/theme/theme";
import DeletePopup from "./delete";
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";

interface ProvisionGroupMember {
    id: number;
    description: string;
    type: string;
    amount: string | number;
    categoryId: number;
    startDate?: string;
    endDate?: string;
}

interface ProvisionGroupDetailProps {
    open: boolean;
    groupName: string;
    members: ProvisionGroupMember[];
    categories: DropdownOption[];
    onClose: () => void;
    onUngroup: () => void;
    onRemoveMembers: (ids: number[]) => void;
    canUpdate: boolean;
}

const getRecurrenceColor = (recurrence: string) => {
    switch (recurrence) {
        case 'daily': return '#FF5722';
        case 'weekly': return '#3F51B5';
        case 'fortnightly': return '#2196F3';
        case 'monthly': return 'var(--color-primary)';
        case 'quarterly': return '#9C27B0';
        case 'half-yearly': return '#FF9800';
        case 'yearly': return '#4CAF50';
        case 'custom': return 'var(--color-primary)';
        default: return '#c7c7c7';
    }
};

const ProvisionGroupDetail: React.FC<ProvisionGroupDetailProps> = ({ open, groupName, members, categories, onClose, onUngroup, onRemoveMembers, canUpdate }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { formatDate, formatAmount } = useSettings();
    const [selectedIds, setSelectedIds] = React.useState<number[]>([]);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = React.useState(false);

    React.useEffect(() => {
        if (!open) {
            setSelectedIds([]);
            setConfirmRemoveOpen(false);
        }
    }, [open]);
    // Members refreshing (e.g. after a removal) can drop ids that were
    // selected - keep only ones still present so the button count and any
    // future removal stay accurate.
    React.useEffect(() => {
        setSelectedIds(prev => prev.filter(id => members.some(m => m.id === id)));
    }, [members]);

    const totalAmount = members.reduce((sum, m) => sum + (Number(m.amount) || 0), 0);
    const allSelected = members.length > 0 && selectedIds.length === members.length;
    const someSelected = selectedIds.length > 0 && !allSelected;

    const toggleAll = () => setSelectedIds(allSelected ? [] : members.map(m => m.id));
    const toggleOne = (id: number) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const columns = React.useMemo<MRT_ColumnDef<ProvisionGroupMember>[]>(() => [
        {
            id: 'select',
            header: '',
            size: 40,
            enableColumnActions: false,
            // Plain MUI Checkbox has ~9px built-in padding around a 24px
            // icon (~42px hit target) - taller than the compact text cells
            // around it, which was what was actually stretching the whole
            // header row, not the cell padding itself.
            Header: () => (
                <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleAll}
                    disabled={!canUpdate}
                    sx={{ p: 0 }}
                />
            ),
            Cell: ({ row }) => (
                <Checkbox
                    size="small"
                    checked={selectedIds.includes(row.original.id)}
                    onChange={() => toggleOne(row.original.id)}
                    disabled={!canUpdate}
                    sx={{ p: 0 }}
                />
            ),
        },
        {
            accessorKey: 'description',
            header: 'Description',
            size: 200,
            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{cell.getValue<string>()}</Typography>,
        },
        {
            accessorKey: 'type',
            header: 'Recurrence',
            size: 110,
            Cell: ({ cell }) => {
                const value = cell.getValue<string>();
                return (
                    <span style={{ background: getRecurrenceColor(value), borderRadius: 12, padding: '2px 8px', color: '#fff', fontSize: 11, letterSpacing: '0.5px', fontWeight: 600 }}>
                        {value?.toUpperCase()}
                    </span>
                );
            },
        },
        {
            accessorKey: 'categoryId',
            header: 'Category',
            size: 140,
            Cell: ({ cell }) => <Typography fontSize={12}>{categories.find(c => String(c.value) === String(cell.getValue<number>()))?.label || '-'}</Typography>,
        },
        {
            accessorKey: 'startDate',
            header: 'Start Date',
            size: 100,
            Cell: ({ cell }) => <Typography fontSize={12}>{cell.getValue<string>() ? formatDate(new Date(cell.getValue<string>())) : '-'}</Typography>,
        },
        {
            accessorKey: 'endDate',
            header: 'End Date',
            size: 100,
            Cell: ({ cell }) => <Typography fontSize={12}>{cell.getValue<string>() ? formatDate(new Date(cell.getValue<string>())) : '-'}</Typography>,
        },
        {
            accessorKey: 'amount',
            header: 'Amount',
            size: 110,
            Cell: ({ cell }) => <Typography fontSize={12} fontWeight={600}>{formatAmount?.(Number(cell.getValue<string | number>()))}</Typography>,
        },
    ], [categories, formatDate, formatAmount, selectedIds, allSelected, someSelected, canUpdate, members]);

    return (
        <>
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth={isMobile ? false : "md"}
            fullWidth
            {...(isMobile ? { TransitionComponent: SlideUpTransition } : {})}
            sx={isMobile ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
            PaperProps={{
                sx: isMobile
                    ? { m: 0, width: '100%', maxWidth: '100%', height: '90vh', maxHeight: '90vh', borderTopLeftRadius: 16, borderTopRightRadius: 16, borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                    : { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: 22, pb: 1 }}>
                <Users size={20} color="var(--color-primary)" />
                {groupName}
                <Typography component="span" sx={{ fontSize: 14, color: 'var(--color-text-muted)', fontWeight: 400 }}>
                    ({members.length} provisions)
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ py: 0 }}>
                <MaterialReactTable
                    columns={columns}
                    data={members}
                    enablePagination={false}
                    enableTopToolbar={false}
                    enableBottomToolbar={false}
                    enableColumnActions={false}
                    enableSorting={false}
                    initialState={{ density: 'compact' }}
                    muiTableContainerProps={{ sx: { maxHeight: isMobile ? 250 : 400, overflowY: 'auto' } }}
                    muiTablePaperProps={{ sx: { boxShadow: 'none' } }}
                    muiTableHeadCellProps={{ sx: { background: 'var(--color-container-background)' } }}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    Total: {formatAmount?.(totalAmount)}
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                    {selectedIds.length > 0 && (
                        <Button
                            onClick={() => setConfirmRemoveOpen(true)}
                            disabled={!canUpdate}
                            sx={{ textTransform: 'none', fontWeight: 600, color: 'var(--color-negative-amount)' }}
                        >
                            Remove Selected ({selectedIds.length})
                        </Button>
                    )}
                    <Button onClick={onUngroup} disabled={!canUpdate} sx={{ textTransform: 'none', color: 'var(--color-text-muted)' }}>
                        Ungroup All
                    </Button>
                    <Button variant="contained" onClick={onClose} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, background: 'var(--color-primary)', color: '#fff', boxShadow: 'none', '&:hover': { background: 'var(--color-primary)' } }}>
                        Close
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
        <DeletePopup
            open={confirmRemoveOpen}
            message={`Remove ${selectedIds.length} Provision${selectedIds.length !== 1 ? 's' : ''}?`}
            description={`${selectedIds.length !== 1 ? 'These provisions' : 'This provision'} will be taken out of this group and shown as individual rows again. This doesn't delete them.`}
            confirmLabel="Remove"
            onCancel={() => setConfirmRemoveOpen(false)}
            onConfirm={() => {
                onRemoveMembers(selectedIds);
                setConfirmRemoveOpen(false);
            }}
        />
        </>
    );
};

export default ProvisionGroupDetail;
